import { useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AdditionalItemsForm from './additionalItemsForm.js';
import AdditionalInfoModal from './additionalInfoModal/additionalInfoModal.js';
import {
  jurisdictionOptions,
  reportTypeOptions,
} from '../../../../../data/forms/nrc/stolenAndMissingDropdownOptions.js';
import RetailerLookupButton from '../retailerLookupButton.js';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const StolenAndMissingInstantTicketForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: '',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    reportType: '',
    dateTimeCall: dayjs.utc(),
    callersName: '',
    poc: '',
    jobTitle: '',
    description: '',
    howTicketNumbersReported: '',
    calledRetailerBack: '',
    accuracyUncertainty: '',
    encouragedRetailer: '',
    caseNum: '',
    lotterySecurityCallback: '',
    reportedToPolice: '',
    policeAgencyName: '',
    policeOfficerName: '',
    policeOfficerPhone: '',
    dateTimeTheft: null,
    ticketLastLocation: '',
    ticketSuspectedStatus: '',
    packsMissing: '',
    packTypes: '',
    advisedFee: '',
    advisedPoliceReport: '',
    hoursOfOperation: '',
    comments: '',
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const [entries, setEntries] = useState([
    {
      id: 1,
      gameName: '',
      gameNumber: '',
      packNumber: '',
      beginning: '',
      ending: '',
      packStatus: '',
    },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdditionalInfoModalOpen, setIsAdditionalInfoModalOpen] =
    useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (name === 'jurisdiction') {
      setForm({
        ...form,
        [name]: value,
        // Reset fields that may not be relevant for the new jurisdiction
        reportedToPolice: '',
        policeAgencyName: '',
        policeOfficerName: '',
        policeOfficerPhone: '',
        ticketLastLocation: '',
        ticketSuspectedStatus: '',
        packsMissing: '',
        packTypes: '',
        advisedFee: '',
        advisedPoliceReport: '',
        hoursOfOperation: '',
        howTicketNumbersReported: '',
        calledRetailerBack: '',
        accuracyUncertainty: '',
        encouragedRetailer: '',
      });

      for (let entry of entries) {
        entry.packStatus = '';
      }
    }
  };

  const handleDateTimeCallChange = value => {
    setForm({
      ...form,
      dateTimeCall: value,
    });
  };

  const handleDateTimeTheftChange = value => {
    setForm({
      ...form,
      dateTimeTheft: value,
    });
  };

  const handleAdditionalInfoModalOpenClose = () => {
    setIsAdditionalInfoModalOpen(!isAdditionalInfoModalOpen);
  };

  const handleClear = () => {
    setForm({
      ...form,
      jurisdiction: '',
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      reportType: '',
      dateTimeCall: dayjs.utc(),
      callersName: '',
      poc: '',
      jobTitle: '',
      description: '',
      howTicketNumbersReported: '',
      calledRetailerBack: '',
      accuracyUncertainty: '',
      encouragedRetailer: '',
      caseNum: '',
      lotterySecurityCallback: '',
      reportedToPolice: '',
      policeAgencyName: '',
      policeOfficerName: '',
      policeOfficerPhone: '',
      dateTimeTheft: null,
      ticketLastLocation: '',
      ticketSuspectedStatus: '',
      packsMissing: '',
      packTypes: '',
      advisedFee: '',
      advisedPoliceReport: '',
      hoursOfOperation: '',
      comments: '',
    });

    setEntries(prevEntries =>
      prevEntries.map(entry => ({
        ...entry,
        gameName: '',
        gameNumber: '',
        packNumber: '',
        beginning: '',
        ending: '',
        packStatus: '',
      })),
    );
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/uscc/stolenTickets/submit`,
        data: {
          ...form,
          additionalEntries: entries,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      jurisdiction,
      retailerId,
      retailerName,
      address,
      phoneNumber,
      reportType,
      dateTimeCall,
      callersName,
      poc,
      jobTitle,
      description,
      howTicketNumbersReported,
      calledRetailerBack,
      accuracyUncertainty,
      encouragedRetailer,
      lotterySecurityCallback,
      reportedToPolice,
      policeAgencyName,
      policeOfficerName,
      policeOfficerPhone,
      dateTimeTheft,
      ticketLastLocation,
      ticketSuspectedStatus,
      packsMissing,
      packTypes,
      advisedFee,
      advisedPoliceReport,
      hoursOfOperation,
    } = form;

    if (
      !jurisdiction ||
      !retailerId ||
      !retailerName ||
      !address ||
      !phoneNumber ||
      !callersName ||
      !dateTimeTheft ||
      !reportType ||
      !dateTimeCall ||
      !poc ||
      !jobTitle ||
      !description
    ) {
      return false;
    }

    if (jurisdiction === 'Rhode Island' && !hoursOfOperation) {
      return false;
    }

    if (jurisdiction === 'Wisconsin') {
      if (!reportedToPolice || !ticketLastLocation || !ticketSuspectedStatus) {
        return false;
      }

      if (reportedToPolice === 'Yes') {
        if (!policeAgencyName || !policeOfficerName || !policeOfficerPhone) {
          return false;
        }
      }
    }

    if (jurisdiction === 'Rhode Island') {
      if (
        !packTypes ||
        !packsMissing ||
        !encouragedRetailer ||
        !advisedPoliceReport
      ) {
        return false;
      }
    }

    if (jurisdiction !== 'Wisconsin' && jurisdiction !== 'Rhode Island') {
      if (
        !packTypes ||
        !packsMissing ||
        !lotterySecurityCallback ||
        !howTicketNumbersReported ||
        !encouragedRetailer
      ) {
        return false;
      }
    }

    if (
      jurisdiction !== 'Wisconsin' &&
      jurisdiction !== 'Rhode Island' &&
      jurisdiction !== 'Washington'
    ) {
      if (!accuracyUncertainty) {
        return false;
      }
    }

    if (
      jurisdiction !== 'Wisconsin' &&
      jurisdiction !== 'Rhode Island' &&
      jurisdiction !== 'Washington' &&
      jurisdiction !== 'Nebraska' &&
      !calledRetailerBack
    ) {
      return false;
    }

    if (
      jurisdiction !== 'Wisconsin' &&
      jurisdiction !== 'Rhode Island' &&
      jurisdiction !== 'Washington' &&
      jurisdiction !== 'Nebraska' &&
      jurisdiction !== 'New York' &&
      !advisedFee
    ) {
      return false;
    }

    for (let entry of entries) {
      const {
        gameName,
        gameNumber,
        packNumber,
        beginning,
        ending,
        packStatus,
      } = entry;

      if (!gameName || !gameNumber || !packNumber || !beginning || !ending) {
        return false;
      }

      if (jurisdiction === 'New York' && !packStatus) {
        return false;
      }
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>
              {translations.jurisdiction || 'Jurisdiction'}
            </InputLabel>
            <Select
              value={form.jurisdiction}
              label={translations.jurisdiction || 'Jurisdiction'}
              onChange={handleChange}
              name='jurisdiction'
              endAdornment={
                <InputAdornment position='end'>
                  <Tooltip
                    title={
                      form.jurisdiction
                        ? 'Fill out required Additional Information'
                        : ''
                    }
                    arrow
                  >
                    <Button
                      variant={'contained'}
                      disabled={!form.jurisdiction}
                      onClick={handleAdditionalInfoModalOpenClose}
                      sx={{
                        backgroundColor: 'var(--procyon-green)',
                        color: '#000000',
                        fontFamily: 'BrightstarSans-Regular',
                      }}
                    >
                      Additional Info
                    </Button>
                  </Tooltip>
                </InputAdornment>
              }
            >
              {jurisdictionOptions.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={'Enter a Retailer ID (numeric values only) to lookup'}
            arrow
          >
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Retailer Name'
            name='retailerName'
            required
            value={form.retailerName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Address'
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Phone Number'
            name='phoneNumber'
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phoneNumber}
            onChange={handleChange}
          />
          {form.jurisdiction === 'Rhode Island' && (
            <TextField
              label='Hours of Operation'
              name='hoursOfOperation'
              required
              fullWidth
              value={form.hoursOfOperation}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          )}
          <TextField
            label="Caller's Name"
            name='callersName'
            required
            fullWidth
            value={form.callersName}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Tooltip
              title={'When did the theft occur?'}
              arrow
            >
              <div>
                <DateTimePicker
                  label='Date and Time of Theft*'
                  required
                  value={form.dateTimeTheft}
                  onChange={value => handleDateTimeTheftChange(value)}
                  sx={{
                    backgroundColor: '#ffffff',
                    marginBottom: '15px',
                    width: '100%',
                  }}
                />
              </div>
            </Tooltip>
          </LocalizationProvider>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Report Type'}</InputLabel>
            <Select
              value={form.reportType}
              label={'Report Type'}
              onChange={handleChange}
              name='reportType'
            >
              {reportTypeOptions.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Tooltip
              title={'Date and Time call came into the NRC for reported theft'}
              arrow
            >
              <div>
                <DateTimePicker
                  label='Date and Time of Call (UTC)*'
                  required
                  disabled
                  value={form.dateTimeCall}
                  onChange={value => handleDateTimeCallChange(value)}
                  sx={{
                    backgroundColor: '#ffffff',
                    marginBottom: '15px',
                    width: '100%',
                  }}
                />
              </div>
            </Tooltip>
          </LocalizationProvider>
          <TextField
            label='Point of Contact'
            name='poc'
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.poc}
            onChange={handleChange}
          />
          <TextField
            label='Job Title'
            name='jobTitle'
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.jobTitle}
            onChange={handleChange}
          />
          <TextField
            label='Brief Description of how theft happened'
            name='description'
            value={form.description}
            onChange={handleChange}
            multiline
            required
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <AdditionalItemsForm
          mainForm={form}
          entries={entries}
          setEntries={setEntries}
        />
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              !isFormValid()
                ? 'Please fill in all required fields to enable the Submit button'
                : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      {isAdditionalInfoModalOpen && (
        <AdditionalInfoModal
          isOpen={isAdditionalInfoModalOpen}
          handleClose={handleAdditionalInfoModalOpenClose}
          jid={form.jurisdiction}
          handleChange={handleChange}
          form={form}
        />
      )}
    </>
  );
};

export default StolenAndMissingInstantTicketForm;
